import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTRPCClient } from "utils/trpc"

type BaseFavorite = {
  id: string
}

export type ProjectFavorite = BaseFavorite & {
  type: "project"
  data: {
    name: string | null
    href: string
    logo: string | null
    symbol: string | null
  }
}

export type DashboardFavorite = BaseFavorite & {
  type: "dashboard"
  data: {
    name: string | null
    description: string | null
    href: string
    logo: string | null
    isOwner: boolean
  }
}

export type ChartFavorite = BaseFavorite & {
  type: "chart"
  data: {
    name: string | null
    href: string
    logo: string | null
    isOwner: boolean
  }
}

export type Favorite = DashboardFavorite | ProjectFavorite | ChartFavorite

/**
 * A top-level hook for managing all types of favorites via local storage.
 * @todo Should move this db persistence so it sticks with a user across browsers.
 */

export const useFavorites = () => {
  const trpcClient = useTRPCClient()
  const queryClient = useQueryClient()

  const { data: { favorites } = { favorites: [] } } = useQuery({
    queryKey: ["favorites"],
    queryFn: () =>
      trpcClient.users.getOrgUserFavorites.query(undefined, {
        context: {
          skipBatch: true,
        },
      }),
  })

  const { mutate: setFavorites } = useMutation({
    mutationFn: (favorites: Favorite[]) =>
      trpcClient.users.postOrgUserFavorites.mutate({ favorites }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["favorites"],
      })
    },
  })

  return [favorites, setFavorites] as [
    Favorite[],
    React.Dispatch<React.SetStateAction<Favorite[]>>,
  ]
}
